
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Check } from 'lucide-react';

function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true
    analytics: false,
    functional: false,
  });

  useEffect(() => {
    const storedConsent = localStorage.getItem('cookieConsent');
    if (!storedConsent) {
      setIsVisible(true);
    } else {
      try {
        const parsed = JSON.parse(storedConsent);
        setPreferences(parsed);
      } catch (e) {
        setIsVisible(true);
      }
    }

    // Listen for custom event to reopen modal from other components
    const handleOpenSettings = () => setShowModal(true);
    window.addEventListener('open-cookie-settings', handleOpenSettings);
    return () => window.removeEventListener('open-cookie-settings', handleOpenSettings);
  }, []);

  const saveConsent = (newPreferences) => {
    const consentData = {
      ...newPreferences,
      accepted: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setPreferences(newPreferences);
    setIsVisible(false);
    setShowModal(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      functional: true,
    });
  };

  const handleRejectAll = () => {
    saveConsent({
      essential: true,
      analytics: false,
      functional: false,
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <AnimatePresence>
        {isVisible && !showModal && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6 pointer-events-none"
          >
            <div className="max-w-5xl mx-auto bg-background/95 backdrop-blur-md border border-border shadow-2xl rounded-2xl p-6 pointer-events-auto flex flex-col md:flex-row gap-6 items-center justify-between">
              <div className="flex-1 space-y-2">
                <h3 className="text-lg font-bold text-foreground font-elegant">Respect de votre vie privée</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Nous utilisons des cookies pour améliorer votre expérience sur Neuvième Continent, analyser notre trafic et personnaliser le contenu. Vous pouvez accepter tous les cookies, les refuser, ou gérer vos préférences. Consultez notre <a href="/cookie-policy" className="text-primary hover:underline">Politique de Cookies</a> pour plus de détails.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
                <button
                  onClick={() => setShowModal(true)}
                  className="px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-xl transition-colors"
                >
                  Gérer les préférences
                </button>
                <button
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-sm font-medium text-foreground border border-border hover:bg-muted rounded-xl transition-colors"
                >
                  Tout refuser
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors shadow-md"
                >
                  Tout accepter
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6 pointer-events-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-background/80 backdrop-blur-sm"
              onClick={() => !isVisible && setShowModal(false)}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-2xl bg-card border border-border shadow-2xl rounded-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              <div className="p-6 border-b border-border flex items-center justify-between bg-muted/30">
                <h2 className="text-xl font-bold font-elegant">Préférences de Cookies</h2>
                {!isVisible && (
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-muted rounded-full transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
              
              <div className="p-6 overflow-y-auto custom-scrollbar space-y-6">
                <div className="space-y-4">
                  {/* Essential */}
                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-border bg-muted/10">
                    <div>
                      <h4 className="font-semibold text-foreground">Cookies Essentiels</h4>
                      <p className="text-sm text-muted-foreground mt-1">Nécessaires au fonctionnement du site. Ils ne peuvent pas être désactivés.</p>
                    </div>
                    <div className="shrink-0 pt-1">
                      <div className="w-11 h-6 bg-primary rounded-full relative opacity-50 cursor-not-allowed">
                        <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-border hover:border-primary/30 transition-colors">
                    <div>
                      <h4 className="font-semibold text-foreground">Cookies Analytiques</h4>
                      <p className="text-sm text-muted-foreground mt-1">Nous aident à comprendre comment les visiteurs interagissent avec le site.</p>
                    </div>
                    <button 
                      onClick={() => togglePreference('analytics')}
                      className="shrink-0 pt-1 focus:outline-none"
                    >
                      <div className={`w-11 h-6 rounded-full relative transition-colors duration-200 ${preferences.analytics ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${preferences.analytics ? 'translate-x-6' : 'translate-x-1'}`} />
                      </div>
                    </button>
                  </div>

                  {/* Functional */}
                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-border hover:border-primary/30 transition-colors">
                    <div>
                      <h4 className="font-semibold text-foreground">Cookies Fonctionnels</h4>
                      <p className="text-sm text-muted-foreground mt-1">Permettent de mémoriser vos préférences et de personnaliser votre expérience.</p>
                    </div>
                    <button 
                      onClick={() => togglePreference('functional')}
                      className="shrink-0 pt-1 focus:outline-none"
                    >
                      <div className={`w-11 h-6 rounded-full relative transition-colors duration-200 ${preferences.functional ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${preferences.functional ? 'translate-x-6' : 'translate-x-1'}`} />
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-border bg-muted/30 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-2.5 text-sm font-medium text-foreground border border-border hover:bg-muted rounded-xl transition-colors"
                >
                  Enregistrer mes choix
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2.5 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors shadow-md"
                >
                  Tout accepter
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

export default CookieConsentBanner;
